import React, { useState, useEffect, useCallback, useMemo } from "react";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";
import { Loader2, AlertCircle, FileText, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import KnownQuestionsPanel from "./KnownQuestionsPanel";
import QuestionOrderPanel from "./QuestionOrderPanel";
import QuestionForm from "./QuestionForm";
import CallScriptTemplatePicker from "./CallScriptTemplatePicker";
import { applyCallScriptTemplate } from "./applyCallScriptTemplate";
import {
  CALL_SCRIPT_TEMPLATES,
  filterCallScriptTemplatesBySpecialty,
} from "./callScriptTemplates";
import {
  buildCategoriesFromQuestions,
  filterKnownQuestionsBySpecialty,
} from "./filterKnownQuestionsBySpecialty";
import {
  updateCustomQuestionProcedureCode,
} from "./customizeProcedureCodeQuestion";
import { getCallScript, saveCallScript, type SaveCallScriptRequest } from "utils/callScriptsApi";

const DEFAULT_TEMPLATE_PROCEDURE_CODE = "11111";

export interface KnownQuestion {
  id: string;
  question_text: string;
  category: string;
  expected_response_type: string;
  results_field_path: string | null;
  procedure_code: string | null;
  is_custom: boolean;
  /** Bank question text with XYZ placeholder; used to re-apply procedure code edits. */
  procedure_code_source_text?: string | null;
  procedure_code_source_path?: string | null;
}

export interface CustomQuestion extends KnownQuestion {}

interface KnownQuestionsResponse {
  known_questions: KnownQuestion[];
  categories: Record<string, string[]>;
}

interface CallScriptManagerProps {
  mode: "onboarding" | "account";
  teamId: string;
  specialty?: string | null;
  callScriptId?: string;
  onNext?: (data: { call_script_id: string }) => void;
  onSave?: () => void;
  onDelete?: () => void;
}

const CallScriptManager: React.FC<CallScriptManagerProps> = ({
  mode,
  teamId,
  specialty,
  callScriptId: propCallScriptId,
  onNext,
  onSave,
  onDelete,
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [knownQuestions, setKnownQuestions] = useState<KnownQuestion[]>([]);
  const [knownQuestionsForLookup, setKnownQuestionsForLookup] = useState<KnownQuestion[]>([]);
  const [categories, setCategories] = useState<Record<string, string[]>>({});
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [orderedQuestionIds, setOrderedQuestionIds] = useState<string[]>([]);
  const [callScriptId, setCallScriptId] = useState<string | null>(propCallScriptId || null);
  const [callScriptName, setCallScriptName] = useState<string>("");
  const [additionalInfo, setAdditionalInfo] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateProcedureCode, setTemplateProcedureCode] = useState(DEFAULT_TEMPLATE_PROCEDURE_CODE);
  const [templateProcedureQuestionIds, setTemplateProcedureQuestionIds] = useState<Set<string>>(new Set());

  const visibleCallScriptTemplates = useMemo(
    () => filterCallScriptTemplatesBySpecialty(CALL_SCRIPT_TEMPLATES, specialty),
    [specialty],
  );

  // Fetch known questions (filtered by user specialty)
  const fetchKnownQuestions = useCallback(async (specialty: string | null) => {
    try {
      const response = await baseApi.get<KnownQuestionsResponse>(
        "/dashboard/call_scripts/known_questions",
        { withCredentials: true }
      );
      const filteredQuestions = filterKnownQuestionsBySpecialty(
        response.data.known_questions,
        specialty,
      );
      setKnownQuestions(filteredQuestions);
      setKnownQuestionsForLookup(filteredQuestions);
      setCategories(buildCategoriesFromQuestions(filteredQuestions));
    } catch (err: any) {
      console.error("Error fetching known questions:", err);
      setError("Failed to load known questions. Please try again.");
      toast.error("Failed to load known questions", {
        description: err.response?.data?.detail || "An unexpected error occurred.",
      });
    }
  }, []);

  // Helper function to find and collect call details questions
  const getCallDetailsQuestions = useCallback((
    knownQuestionsList: KnownQuestion[],
    customQuestionsList: CustomQuestion[]
  ) => {
    const repNameQuestion = knownQuestionsList.find(
      (q) => q.results_field_path === "call_details[0].representative_name"
    );
    const refNumberQuestion = knownQuestionsList.find(
      (q) => q.results_field_path === "call_details[0].reference_number"
    );

    // Find custom CallDetails questions
    const customCallDetailsQuestionIds = customQuestionsList
      .filter((q) => q.category === "CallDetails")
      .map((q) => q.id);

    // Collect all call details question IDs (custom first, then known)
    const allCallDetailsIds: string[] = [];
    allCallDetailsIds.push(...customCallDetailsQuestionIds);
    if (repNameQuestion) allCallDetailsIds.push(repNameQuestion.id);
    if (refNumberQuestion) allCallDetailsIds.push(refNumberQuestion.id);

    return {
      repNameQuestion,
      refNumberQuestion,
      allCallDetailsIds,
    };
  }, []);

  const fetchCallScript = useCallback(async () => {
    if (!propCallScriptId) return;

    try {
      const response = await getCallScript(propCallScriptId);

      if (response.call_script_id) {
        setCallScriptId(response.call_script_id);
        setCallScriptName(response.name || "");
        setAdditionalInfo(response.additional_info || "");

        // Separate known and custom questions
        const knownIds = new Set<string>();
        const customQuestionsList: CustomQuestion[] = [];

        response.questions.forEach((q) => {
          if (q.is_custom) {
            customQuestionsList.push(q);
          } else {
            knownIds.add(q.id);
          }
        });

        setKnownQuestionsForLookup((prev) => {
          const byId = new Map(prev.map((q) => [q.id, q]));
          response.questions
            .filter((q) => !q.is_custom)
            .forEach((q) => byId.set(q.id, q));
          return Array.from(byId.values());
        });

        setSelectedQuestionIds(knownIds);
        setCustomQuestions(customQuestionsList);

        // Ensure call details questions are at the bottom
        setOrderedQuestionIds(response.ordered_question_ids);
      }
    } catch (err: any) {
      console.error("Error fetching call script:", err);

      if (err.response?.status !== 404) {
        toast.error("Failed to load call script", {
          description: err.response?.data?.detail || "An unexpected error occurred.",
        });
      }
    }
  }, [propCallScriptId]);

  // Ensure call details questions are at bottom and selected after initial data load
  useEffect(() => {
    if (knownQuestions.length === 0 || loading) return;

    const { repNameQuestion, refNumberQuestion, allCallDetailsIds } =
      getCallDetailsQuestions(knownQuestions, customQuestions);

    if (allCallDetailsIds.length === 0) return;

    // Update selectedQuestionIds to include known call details questions
    setSelectedQuestionIds((prev) => {
      const updated = new Set(prev);
      if (repNameQuestion) updated.add(repNameQuestion.id);
      if (refNumberQuestion) updated.add(refNumberQuestion.id);
      if (updated.size === prev.size &&
          (!repNameQuestion || prev.has(repNameQuestion.id)) &&
          (!refNumberQuestion || prev.has(refNumberQuestion.id))) {
        return prev;
      }
      return updated;
    });

    // Update orderedQuestionIds to move all call details questions to bottom
    setOrderedQuestionIds((prev) => {
      let updated = prev.filter((id) => !allCallDetailsIds.includes(id));
      updated.push(...allCallDetailsIds);
      if (updated.length === prev.length &&
          updated.every((id, idx) => id === prev[idx])) {
        return prev;
      }
      return updated;
    });
  }, [knownQuestions, customQuestions, loading, getCallDetailsQuestions]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      await Promise.all([
        fetchKnownQuestions(specialty ?? null),
        fetchCallScript(),
      ]);

      setLoading(false);
    };

    fetchData();
  }, [fetchKnownQuestions, fetchCallScript, specialty]);

  // Save call script
  const handleSave = useCallback(async () => {
    if (mode === "account" && !callScriptName.trim()) {
      toast.error("Call script name is required");
      setError("Please provide a name for your call script.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Find known call details questions (representative_name and reference_number)
      const { repNameQuestion, refNumberQuestion, allCallDetailsIds } =
        getCallDetailsQuestions(knownQuestions, customQuestions);

      // Rebuild order by category to ensure questions stay with their categories
      // This preserves order within each category from orderedQuestionIds
      const allQuestionsMap = new Map<string, KnownQuestion | CustomQuestion>();
      knownQuestions.forEach((q) => allQuestionsMap.set(q.id, q));
      customQuestions.forEach((q) => allQuestionsMap.set(q.id, q));

      // Group questions by category while preserving order within each category
      const questionsByCategory: Record<string, string[]> = {};
      const callDetailsSet = new Set(allCallDetailsIds);

      orderedQuestionIds.forEach((id) => {
        if (callDetailsSet.has(id)) return; // Skip CallDetails, will add at end

        const question = allQuestionsMap.get(id);
        if (question) {
          const category = question.category;
          if (!questionsByCategory[category]) {
            questionsByCategory[category] = [];
          }
          questionsByCategory[category].push(id);
        }
      });

      // Build final order: categories in order, then CallDetails at the end
      // For BenefitsDetails, group questions by procedure_code
      const CATEGORY_ORDER = [
        "PlanInformation",
        "Maximums",
        "ClaimsInformation",
        "BenefitsDetails",
        "ExtraDentalInfo",
        "ExtraMedicalInfo",
      ];

      const finalOrderedIds: string[] = [];

      // Helper function to group BenefitsDetails questions by procedure_code
      const groupBenefitsDetailsByProcedureCode = (questionIds: string[]): string[] => {
        const grouped: Record<string, string[]> = {};
        const noProcedureCode: string[] = [];

        questionIds.forEach((id) => {
          const question = allQuestionsMap.get(id);
          if (question && question.category === "BenefitsDetails") {
            const procCode = question.procedure_code || "";
            if (procCode) {
              if (!grouped[procCode]) {
                grouped[procCode] = [];
              }
              grouped[procCode].push(id);
            } else {
              noProcedureCode.push(id);
            }
          }
        });

        // Return: questions without procedure_code first, then grouped by procedure_code
        const result: string[] = [...noProcedureCode];
        Object.keys(grouped).sort().forEach((procCode) => {
          result.push(...grouped[procCode]);
        });
        return result;
      };

      // Add categories in order
      CATEGORY_ORDER.forEach((category) => {
        if (questionsByCategory[category]) {
          if (category === "BenefitsDetails") {
            // Group BenefitsDetails questions by procedure_code
            finalOrderedIds.push(...groupBenefitsDetailsByProcedureCode(questionsByCategory[category]));
          } else {
            finalOrderedIds.push(...questionsByCategory[category]);
          }
        }
      });

      // Add any categories not in CATEGORY_ORDER
      Object.keys(questionsByCategory).forEach((category) => {
        if (!CATEGORY_ORDER.includes(category)) {
          if (category === "BenefitsDetails") {
            finalOrderedIds.push(...groupBenefitsDetailsByProcedureCode(questionsByCategory[category]));
          } else {
            finalOrderedIds.push(...questionsByCategory[category]);
          }
        }
      });

      // Finally, add CallDetails at the end
      finalOrderedIds.push(...allCallDetailsIds);

      // Ensure selectedQuestionIds includes known call details questions
      const updatedSelectedIds = new Set(selectedQuestionIds);
      if (repNameQuestion) {
        updatedSelectedIds.add(repNameQuestion.id);
      }
      if (refNumberQuestion) {
        updatedSelectedIds.add(refNumberQuestion.id);
      }

      // Prepare custom questions for API
      // Questions with results_field_path set will map to Results object fields (like known questions)
      // Questions without results_field_path will go to custom_answers dict
      const questionsToSave = customQuestions.map((q) => ({
        id: q.id,
        question_text: q.question_text,
        category: q.category,
        expected_response_type: q.expected_response_type,
        results_field_path: q.results_field_path,
        procedure_code: q.procedure_code,
        is_custom: q.is_custom ?? true,
      }));

      const request: SaveCallScriptRequest = {
        team_id: teamId,
        call_script_id: callScriptId || undefined,
        name: mode === "onboarding" ? "Default Call Script" : callScriptName.trim(),
        ordered_question_ids: finalOrderedIds,
        additional_info: additionalInfo.trim() || null,
        questions: questionsToSave,
      };

      const response = await saveCallScript(request);

      // Update local state to reflect call details questions being selected and at bottom
      if (allCallDetailsIds.length > 0) {
        setSelectedQuestionIds(updatedSelectedIds);
        setOrderedQuestionIds(finalOrderedIds);
      }

      // Update call script ID if it was created
      if (response.call_script_id) {
        setCallScriptId(response.call_script_id);
      }

      toast.success(callScriptId ? "Call script updated successfully!" : "Call script created successfully!");

      if (mode === "onboarding" && onNext) {
        onNext({ call_script_id: response.call_script_id });
      } else if (mode === "account" && onSave) {
        onSave();
      }
    } catch (err: any) {
      console.error("Error saving call script:", err);
      const errorMessage = err.response?.data?.detail || "Failed to save call script. Please try again.";
      setError(errorMessage);
      toast.error("Failed to save call script", {
        description: errorMessage,
      });
    } finally {
      setSaving(false);
    }
      }, [teamId, callScriptId, callScriptName, additionalInfo, orderedQuestionIds, customQuestions, selectedQuestionIds, knownQuestions, mode, onNext, onSave, getCallDetailsQuestions]);


  // Toggle question selection
  const toggleQuestionSelection = useCallback((questionId: string) => {
    setSelectedQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
        // Remove from ordered list
        setOrderedQuestionIds((prevOrdered) =>
          prevOrdered.filter((id) => id !== questionId)
        );
      } else {
        newSet.add(questionId);
        // Add to end of ordered list
        setOrderedQuestionIds((prevOrdered) => [...prevOrdered, questionId]);
      }
      return newSet;
    });
  }, []);

  // Add custom question
  const addCustomQuestion = useCallback((questionData: Omit<CustomQuestion, "id">) => {
    const questionId = crypto.randomUUID();

    setOrderedQuestionIds((prev) => {
      const newQuestion: CustomQuestion = {
        ...questionData,
        id: questionId,
        is_custom: true,
      };

      setCustomQuestions((prevQuestions) => [...prevQuestions, newQuestion]);
      return [...prev, questionId];
    });
  }, []);

  const handleAddCustomQuestionFromKnown = useCallback((question: CustomQuestion) => {
    if (!question.results_field_path) {
      console.warn('Customized known question missing results_field_path:', question);
    }

    setCustomQuestions((prev) => [...prev, question]);
    setOrderedQuestionIds((prev) => [...prev, question.id]);
  }, []);

  const updateCustomQuestion = useCallback((questionId: string, questionData: Omit<CustomQuestion, "id">) => {
    setCustomQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            ...questionData,
            // Preserve id
            id: q.id,
          };
        }
        return q;
      })
    );
  }, []);

  // Remove custom question
  const removeCustomQuestion = useCallback((questionId: string) => {
    setCustomQuestions((prev) => prev.filter((q) => q.id !== questionId));
    setOrderedQuestionIds((prev) => prev.filter((id) => id !== questionId));
  }, []);

  // Handle reordering questions
  const handleReorder = useCallback((newOrder: string[]) => {
    setOrderedQuestionIds(newOrder);
  }, []);

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      const template = CALL_SCRIPT_TEMPLATES.find((t) => t.id === templateId);
      if (!template) return;

      const result = applyCallScriptTemplate(
        template,
        knownQuestions,
        templateProcedureCode,
      );
      setSelectedQuestionIds(result.selectedQuestionIds);
      setOrderedQuestionIds(result.orderedQuestionIds);
      setCustomQuestions(result.customQuestions);
      setTemplateProcedureQuestionIds(new Set(result.templateProcedureQuestionIds));
      setSelectedTemplateId(templateId);
    },
    [knownQuestions, templateProcedureCode],
  );

  const handleTemplateProcedureCodeChange = useCallback(
    (value: string) => {
      setTemplateProcedureCode(value);

      if (templateProcedureQuestionIds.size === 0) {
        return;
      }

      setCustomQuestions((prev) =>
        prev.map((q) =>
          templateProcedureQuestionIds.has(q.id)
            ? updateCustomQuestionProcedureCode(q, value)
            : q,
        ),
      );
    },
    [templateProcedureQuestionIds],
  );

  // Skip handler: auto-selects the first known question and creates an autogenerated call script
  const handleSkip = useCallback(async () => {
    const firstQuestion = knownQuestions.find((q) => q.category !== "CallDetails");
    if (!firstQuestion) {
      toast.error("No questions available to auto-select. Please add a question manually.");
      return;
    }

    const { allCallDetailsIds } = getCallDetailsQuestions(knownQuestions, []);
    const orderedIds = [firstQuestion.id, ...allCallDetailsIds];

    setSaving(true);
    try {
      const request: SaveCallScriptRequest = {
        team_id: teamId,
        name: "Auto-generated call script",
        ordered_question_ids: orderedIds,
        additional_info: null,
        questions: [],
      };

      const response = await saveCallScript(request);
      toast.success("Call script created successfully!");

      if (mode === "onboarding" && onNext) {
        onNext({ call_script_id: response.call_script_id });
      }
    } catch (err: any) {
      console.error("Error auto-saving call script:", err);
      const errorMessage = err.response?.data?.detail || "Failed to create call script. Please try again.";
      setError(errorMessage);
      toast.error("Failed to create call script", { description: errorMessage });
    } finally {
      setSaving(false);
    }
  }, [knownQuestions, teamId, mode, onNext, getCallDetailsQuestions]);

  // Handle add custom question button click
  const [showAddQuestionForm, setShowAddQuestionForm] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading call script manager...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !knownQuestions.length) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button type="button" onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  // Empty state for account mode when no call script exists
  const hasNoCallScript = mode === "account" && !callScriptId && !loading && orderedQuestionIds.length === 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header - only show for onboarding or when editing existing call script */}
      {mode === "onboarding" ? (
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Set Up Your Call Script
          </h2>
          <p className="text-gray-600 mt-2">
            Pick a template to prefill questions, or customize manually below.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-7">
              <CallScriptTemplatePicker
                templates={visibleCallScriptTemplates}
                selectedTemplateId={selectedTemplateId}
                onSelect={handleTemplateSelect}
              />
            </div>
            <div className="lg:col-span-5 flex flex-col gap-2 items-end">
              <Label htmlFor="template-procedure-code" className="text-sm text-gray-600">
                Procedure code
              </Label>
              <Input
                id="template-procedure-code"
                type="text"
                value={templateProcedureCode}
                onChange={(e) => handleTemplateProcedureCodeChange(e.target.value)}
                className="w-[112px]"
                placeholder="e.g. 11111"
              />
            </div>
          </div>
        </div>
      ) : (
        mode === "account" &&
        callScriptId && (
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-600">
                Select sample questions and create custom questions for your call scripts.
              </p>
            </div>
            {/* Delete button for account mode when editing existing */}
          </div>
        )
      )}

      {/* Name input for account mode */}
      {mode === "account" && (
        <div className="space-y-2">
          <label htmlFor="call-script-name" className="text-sm font-medium text-gray-700">
            Call Script Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="call-script-name"
            type="text"
            placeholder="e.g., Standard Eligibility Questions"
            value={callScriptName}
            onChange={(e) => setCallScriptName(e.target.value)}
            className="max-w-md"
          />
        </div>
      )}

      {/* Empty State Banner for Account Mode */}
      {hasNoCallScript && (
        <Alert className="border-[#005151]/20 bg-[#005151]/5">
          <FileText className="h-4 w-4 text-[#005151]" />
          <AlertTitle className="text-[#005151]">Get Started with Call Scripts</AlertTitle>
          <AlertDescription className="text-gray-700">
            You don't have a call script yet. Select questions from the panels below or create custom questions,
            then drag to reorder them. Your call script will be used by the AI during insurance calls.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle>Call Script Order</CardTitle>
            <CardDescription>
                Drag to reorder questions
            </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAddQuestionForm(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Custom Question
            </Button>
          </CardHeader>
          <CardContent>
            <QuestionOrderPanel
              orderedQuestionIds={orderedQuestionIds}
              knownQuestions={knownQuestionsForLookup}
              customQuestions={customQuestions}
              onReorder={handleReorder}
              onUpdateCustomQuestion={updateCustomQuestion}
              onRemoveCustomQuestion={removeCustomQuestion}
            />

            {/* Additional Info Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <Label htmlFor="additional-info" className="text-sm font-medium text-gray-700 mb-2 block">
                Additional Information
              </Label>
              <p className="text-xs text-gray-500 mb-3">
                Add any contextual information or notes for inquiries using this call script.
              </p>
              <Textarea
                id="additional-info"
                placeholder="Enter any additional information or notes for callers..."
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                className="min-h-[100px] resize-y"
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader>
            <CardTitle>Sample Questions</CardTitle>
            <CardDescription>
              Select from pre-defined questions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KnownQuestionsPanel
              questions={knownQuestions}
              categories={categories}
              selectedQuestionIds={selectedQuestionIds}
              onToggleSelection={toggleQuestionSelection}
              onAddCustomQuestionFromKnown={handleAddCustomQuestionFromKnown}
            />
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        {mode === "account" && (
          <Button type="button" variant="outline" onClick={() => window.location.reload()}>
            Cancel
          </Button>
        )}
        {mode === "onboarding" && (
          <Button
            type="button"
            variant="outline"
            onClick={handleSkip}
            disabled={saving || knownQuestions.length === 0}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Skipping...
              </>
            ) : (
              "Skip"
            )}
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving || orderedQuestionIds.length === 0}
          className="bg-[#005151] hover:bg-[#004040] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : mode === "onboarding" ? (
            "Continue"
          ) : (
            "Save Call Script"
          )}
        </Button>
      </div>

      {/* Add Custom Question Form Dialog */}
      <QuestionForm
        open={showAddQuestionForm}
        onOpenChange={setShowAddQuestionForm}
        question={null}
        onSave={(questionData) => {
          addCustomQuestion(questionData);
          setShowAddQuestionForm(false);
        }}
      />
    </div>
  );
};

export default CallScriptManager;
